import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    requestActivateSmartReply,
    requestCreateSmartReplyPlan,
    requestCreateSmartReplyPlanItem,
    requestDeleteSmartReplyPlan,
    requestDeleteSmartReplyPlanItem,
    requestPauseSmartReplyPlan,
    requestSmartReplyContactPlanEvent,
    requestSmartReplyContactPlanState,
    requestSmartReplyContactPlans,
    requestSmartReplyPlan,
    requestSmartReplyPlanItems,
    requestSmartReplyPlans,
    requestSmartReplySimulateWebhook,
    requestUpdateSmartReplyPlan,
} from '../ai/ai';
import {
    AiActivateSmartReplyRequest,
    CreateAiSmartReplyPlanItemRequest,
    CreateAiSmartReplyPlanRequest,
    EditAiSmartReplyPlanRequest,
    PlanItemRequestQuery,
    RequestPauseSmartReply,
    RequestSmartReplySimulateWebhook,
} from '../ai/types/Ai';

export const useAiActivateSmartReply = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (props: AiActivateSmartReplyRequest) =>
            (await requestActivateSmartReply(props)).data,
        onSuccess: async data => {
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyContactPlans'] });
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyContactPlanState'] });
        },
    });
};

export const useAiPauseSmartReply = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (props: RequestPauseSmartReply) =>
            (await requestPauseSmartReplyPlan(props.contactId, props.contactPlanId)).data,
        onSuccess: async () => {
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyContactPlans'] });
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyContactPlanState'] });
        },
    });
};

export const useAiSmartReplyPlan = (planId: number) => {
    return useQuery({
        queryKey: ['aiSmartReplyPlan', planId],
        queryFn: async () => {
            return (await requestSmartReplyPlan(planId)).data;
        },
    });
};

export const useAiSmartReplyPlans = () => {
    return useQuery({
        queryKey: ['aiSmartReplyPlans'],
        queryFn: async () => {
            return (await requestSmartReplyPlans()).data;
        },
    });
};

export const useAiSmartReplyPlanItems = (planId: number) => {
    return useQuery({
        queryKey: ['aiSmartReplyPlanItems', planId],
        queryFn: async () => {
            return (await requestSmartReplyPlanItems(planId)).data;
        },
    });
};

export const useAiCreateSmartReplyPlan = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (plan: CreateAiSmartReplyPlanRequest) =>
            (await requestCreateSmartReplyPlan(plan)).data,
        onSuccess: async data => {
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyPlans'] });
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyPlan'] });
        },
    });
};

export const useAiUpdateSmartReplyPlan = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (plan: EditAiSmartReplyPlanRequest) =>
            (await requestUpdateSmartReplyPlan(plan)).data,
        onSuccess: async data => {
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyPlans'] });
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyPlan'] });
        },
    });
};
export const useAiDeleteSmartReplyPlan = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (planId: number) => (await requestDeleteSmartReplyPlan(planId)).data,
        onSuccess: async data => {
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyPlan'] });
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyPlans'] });
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyPlanItems'] });
        },
    });
};

export const useAiCreateSmartReplyPlanItem = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (planItem: CreateAiSmartReplyPlanItemRequest) =>
            (await requestCreateSmartReplyPlanItem(planItem)).data,
        onSuccess: async data => {
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyPlanItems'] });
        },
    });
};

export const useAiDeleteSmartReplyPlanItem = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (queryParams: PlanItemRequestQuery) =>
            (await requestDeleteSmartReplyPlanItem(queryParams)).data,
        onSuccess: async data => {
            queryClient.invalidateQueries({ queryKey: ['aiSmartReplyPlanItems'] });
        },
    });
};

export const useAiSmartReplyContactPlans = (contactId: number) => {
    return useQuery({
        queryKey: ['aiSmartReplyContactPlans', contactId],
        refetchInterval: 10000,
        queryFn: async () => {
            return (await requestSmartReplyContactPlans(contactId)).data;
        },
    });
};

export const useAiSmartReplySimulateWebhook = () => {
    return useMutation({
        mutationFn: async (payload: RequestSmartReplySimulateWebhook) =>
            (await requestSmartReplySimulateWebhook(payload)).data,
    });
};

export const useAiSmartReplyContactPlanState = (contactId: number) => {
    return useQuery({
        queryKey: ['aiSmartReplyContactPlanState', contactId],
        refetchInterval: 30000,
        queryFn: async () => {
            return (await requestSmartReplyContactPlanState(contactId)).data;
        },
    });
};

export const useAiSmartReplyContactPlanEvent = (contactPlanItemId: number) => {
    return useQuery({
        queryKey: ['aiSmartReplyContactPlanEvent', contactPlanItemId],
        queryFn: async () => {
            return (await requestSmartReplyContactPlanEvent(contactPlanItemId)).data;
        },
    });
};
